import React, { useCallback, useEffect, useRef, useState } from "react";

import classNames from "classnames";

import ButtonSmall from "@/common/ButtonSmall/ButtonSmall";
import BrandSwitch from "@/common/Switch/BrandSwitch";
import Text from "@/common/Text/Text";

import { Form, Input, Slider, TimePicker } from "antd";

import ControlPanelActionRow from "../ControlPanelActionRow/ControlPanelActionRow";

import dayjs, { Dayjs } from "dayjs";

import { BrandSoftwareType } from "@/features/ui/types";
import { SyncOutlined } from "@ant-design/icons";
import {
  useGetDeviceDetailQuery,
  useLazyGetDeviceCommandStatusQuery,
  useSendDeviceCommandMutation,
} from "@/features/devices/devicesApi";
import {
  confirmedPlayerSettingsFromDetail,
  isCompleteConfirmedSettings,
  type ConfirmedPlayerSettings,
} from "@/features/devices/confirmedPlayerSettings";
import { DEVICE_DETAIL_QUERY_OPTIONS } from "@/features/devices/deviceDetailDisplay";
import {
  REMOTE_SETTINGS_OPERATOR_COPY,
  remoteSettingsUnsupportedCopy,
} from "@/features/devices/playerAppMetaSupport";
import {
  DEVICE_COMMAND_OPERATOR_COPY,
  type DeviceCommandOperatorState,
  waitForDeviceCommandOperatorResult,
} from "@/features/devices/waitForDeviceCommandOperatorResult";
import {
  REMOTE_SETTINGS_RETRY_LABEL,
  canConfirmRemoteSettings,
  decideOnOpenRefresh,
  lastConfirmedStatusCopy,
  liveFormFieldsFromSnapshot,
  reportKkAppMetaRequest,
  shouldApplyRefreshResult,
} from "@/features/devices/remoteSettingsOnOpenRefresh";
import {
  decideLiveRefreshMs,
  shouldApplyLiveGet,
} from "@/features/devices/remoteSettingsLiveRefresh";
import {
  REMOTE_SETTINGS_CONFIRMATION_UNAVAILABLE,
  SLEEP_TIME_LABEL,
  WAKE_TIME_LABEL,
  WEEKEND_NOT_REPORTED,
  decidePostConfirmReconcile,
  pendingRequestedVsConfirmedCopy,
  requestedFromConfirmValues,
  type RequestedRemoteSettings,
} from "@/features/devices/remoteSettingsConfirmReconcile";
import {
  PIN_PATTERN,
  PIN_PATTERN_MESSAGE,
  PIN_REQUIRED_MESSAGE,
  isPinInputEnabled,
  isPinRequired,
  localPinLifecycleOnAppLockChange,
} from "./localPinLifecycle";
import { MusicSchedulerSection } from "./MusicSchedulerSection";
import styles from "./SettingsForm.module.scss";

interface Props {
  title: string;
  disabled?: boolean;
  ids: string[];
  command: Extract<BrandSoftwareType, "krosskast" | "assistant">;
  /** When false, submitting only resets the form; nothing is transmitted. */
  commandsEnabled: boolean;
}

export interface InitialFormValues {
  volume: number | null;
  master: number | null;
  appLockPin: string | null;
  appLock: boolean;
  wakeSleep: boolean;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  /** T24 — hydrated only from confirmed Fleet snapshot. */
  sleepSaturday: boolean;
  sleepSunday: boolean;
}

type SettingsOperatorUi =
  | DeviceCommandOperatorState
  | "loading"
  | "unsupported"
  | "unavailable"
  | "version-unreported"
  | null;

function operatorLabel(
  state: SettingsOperatorUi,
  installedVersion: string | null | undefined,
  lastConfirmedCopy?: string | null
): string {
  if (state === "loading") return REMOTE_SETTINGS_OPERATOR_COPY.reading;
  if (state === "unsupported") {
    return remoteSettingsUnsupportedCopy(installedVersion);
  }
  if (state === "version-unreported") {
    return REMOTE_SETTINGS_OPERATOR_COPY.versionUnreported;
  }
  if (state === "unavailable") {
    return lastConfirmedCopy || REMOTE_SETTINGS_OPERATOR_COPY.couldNotRead;
  }
  if (state === "sending") return DEVICE_COMMAND_OPERATOR_COPY.sending;
  if (state === "successful") return DEVICE_COMMAND_OPERATOR_COPY.successful;
  if (state === "unsuccessful") return DEVICE_COMMAND_OPERATOR_COPY.unsuccessful;
  return "";
}

export const SettingsForm: React.FC<Props> = ({
  title,
  disabled,
  ids,
  command: _command,
  commandsEnabled,
}) => {
  const deviceId = ids.length === 1 ? ids[0] : null;
  const [fieldIsTouched, setFieldIsTouched] = useState(false);
  const [snapshotComplete, setSnapshotComplete] = useState(false);
  const [confirmedPinSet, setConfirmedPinSet] = useState(false);
  const [operatorState, setOperatorState] = useState<SettingsOperatorUi>(null);
  const [lastConfirmedCopy, setLastConfirmedCopy] = useState<string | null>(null);
  const [pendingRequest, setPendingRequest] = useState<RequestedRemoteSettings | null>(
    null
  );
  const [confirmationUnavailable, setConfirmationUnavailable] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const [musicBusy, setMusicBusy] = useState(false);
  // T24 — null means the device has not reported (NOT confirmed false).
  const [confirmedWeekend, setConfirmedWeekend] = useState<{
    sat: boolean | null;
    sun: boolean | null;
  }>({ sat: null, sun: null });
  const [form] = Form.useForm<InitialFormValues>();
  const reportStartedRef = useRef(false);
  const hydratedRef = useRef(false);
  const generationRef = useRef(0);
  const lastAppliedSyncedAtRef = useRef<string | null>(null);
  const confirmedAtConfirmRef = useRef<ConfirmedPlayerSettings | null>(null);

  const [sendDeviceCommand, { isLoading: isSending }] = useSendDeviceCommandMutation();
  const [getCommandStatus] = useLazyGetDeviceCommandStatusQuery();
  const appMetaBusy = isSending || operatorState === "sending" || operatorState === "loading";
  const isMutatingUpdate = appMetaBusy || musicBusy;
  const liveRefreshMs = decideLiveRefreshMs({
    commandsEnabled,
    hydrated: snapshotComplete && operatorState !== "unavailable" && operatorState !== "unsupported" && operatorState !== "version-unreported",
    fieldIsTouched,
    isMutating: isMutatingUpdate,
  });

  const { data, refetch, isFetching, isSuccess, isError } = useGetDeviceDetailQuery(
    deviceId as string,
    {
      skip: !deviceId || !commandsEnabled,
      ...DEVICE_DETAIL_QUERY_OPTIONS,
      pollingInterval: liveRefreshMs,
    }
  );
  const installedVersion = data?.detail?.player?.version ?? null;
  const commandSurfacesLocked = Boolean(
    disabled || isMutatingUpdate || !snapshotComplete
  );
  const allowLocalPin = !disabled && !isMutatingUpdate;
  const formFullyLocked = Boolean(disabled || isMutatingUpdate);

  const applySnapshot = useCallback(
    (snap: ConfirmedPlayerSettings) => {
      const fields = liveFormFieldsFromSnapshot(snap);
      form.setFieldsValue({
        volume: fields.volume,
        master: fields.master,
        appLock: fields.appLock,
        appLockPin: fields.appLockPin,
        wakeSleep: fields.wakeSleep,
        startDate: fields.startTime ? dayjs(fields.startTime, "HH:mm") : null,
        endDate: fields.endTime ? dayjs(fields.endTime, "HH:mm") : null,
        // Unreported (null) leaves the switch unchecked but NOT confirmed.
        sleepSaturday: fields.sleepSaturday === true,
        sleepSunday: fields.sleepSunday === true,
      });
      setConfirmedWeekend({ sat: fields.sleepSaturday, sun: fields.sleepSunday });
      setConfirmedPinSet(snap.appLockPinSet === true);
      setSnapshotComplete(true);
      setFieldIsTouched(false);
    },
    [form]
  );

  const pollCommand = useCallback(
    async (
      command: "update_kk_app_meta" | "report_kk_app_meta",
      commandId?: string,
      isCurrent?: () => boolean
    ) => {
      return waitForDeviceCommandOperatorResult({
        commandId,
        command,
        getStatus: async (id) => {
          const res = await getCommandStatus({ commandId: id }).unwrap();
          return res.command;
        },
        onState: (state) => {
          if (isCurrent && !isCurrent()) return;
          if (command === "report_kk_app_meta") {
            if (state === "sending" || state === "downloading") {
              setOperatorState("loading");
              return;
            }
            if (state === "unsuccessful") {
              setOperatorState("unavailable");
              return;
            }
            if (state === "successful") {
              setOperatorState("loading");
              return;
            }
          }
          setOperatorState(state);
        },
      });
    },
    [getCommandStatus]
  );

  useEffect(() => {
    reportStartedRef.current = false;
    hydratedRef.current = false;
    lastAppliedSyncedAtRef.current = null;
    confirmedAtConfirmRef.current = null;
    setSnapshotComplete(false);
    setFieldIsTouched(false);
    setOperatorState(null);
    setLastConfirmedCopy(null);
    setPendingRequest(null);
    setConfirmationUnavailable(false);
    setConfirmedWeekend({ sat: null, sun: null });
    generationRef.current += 1;
    return () => {
      generationRef.current += 1;
    };
  }, [deviceId, commandsEnabled]);

  const markUnavailable = useCallback(
    (syncedAt?: string | null, snap?: ConfirmedPlayerSettings | null) => {
      setSnapshotComplete(false);
      setLastConfirmedCopy(
        lastConfirmedStatusCopy({
          syncedAt,
          snap,
        })
      );
      setOperatorState("unavailable");
    },
    []
  );

  useEffect(() => {
    const cached = confirmedPlayerSettingsFromDetail(data?.detail?.player);
    const decision = decideOnOpenRefresh({
      deviceId,
      commandsEnabled,
      reportStarted: reportStartedRef.current,
      isFetching,
      isError,
      isSuccess,
      playerVersion: installedVersion,
      cachedSnapshotComplete: isCompleteConfirmedSettings(cached),
    });
    if (decision.type === "wait" || decision.type === "skip") return;
    reportStartedRef.current = true;
    if (decision.type === "unavailable") {
      markUnavailable(data?.freshness?.confirmedAppMetaSyncedAt, cached);
      return;
    }
    if (decision.type === "unsupported") {
      setSnapshotComplete(false);
      setOperatorState("unsupported");
      return;
    }
    if (decision.type === "version-unreported") {
      setSnapshotComplete(false);
      setOperatorState("version-unreported");
      return;
    }
    const startedAt = ++generationRef.current;
    const reportIsCurrent = () =>
      shouldApplyRefreshResult({
        startedGeneration: startedAt,
        currentGeneration: generationRef.current,
      });
    setOperatorState("loading");
    setSnapshotComplete(false);
    setLastConfirmedCopy(null);
    void (async () => {
      try {
        const sent = await sendDeviceCommand(
          reportKkAppMetaRequest(deviceId as string)
        ).unwrap();
        if (!reportIsCurrent()) {
          return;
        }
        const state = await pollCommand(
          "report_kk_app_meta",
          sent.commandId,
          reportIsCurrent
        );
        if (
          !shouldApplyRefreshResult({
            startedGeneration: startedAt,
            currentGeneration: generationRef.current,
          })
        ) {
          return;
        }
        if (state === "successful") {
          const again = await refetch();
          if (
            !shouldApplyRefreshResult({
              startedGeneration: startedAt,
              currentGeneration: generationRef.current,
            })
          ) {
            return;
          }
          const next = confirmedPlayerSettingsFromDetail(
            again.data?.detail?.player
          );
          if (isCompleteConfirmedSettings(next)) {
            applySnapshot(next);
            hydratedRef.current = true;
            lastAppliedSyncedAtRef.current =
              again.data?.freshness?.confirmedAppMetaSyncedAt ?? null;
            setLastConfirmedCopy(null);
            setOperatorState(null);
          } else {
            markUnavailable(
              again.data?.freshness?.confirmedAppMetaSyncedAt ??
                data?.freshness?.confirmedAppMetaSyncedAt,
              next
            );
          }
        } else {
          markUnavailable(
            data?.freshness?.confirmedAppMetaSyncedAt,
            cached
          );
        }
      } catch {
        if (
          !shouldApplyRefreshResult({
            startedGeneration: startedAt,
            currentGeneration: generationRef.current,
          })
        ) {
          return;
        }
        markUnavailable(data?.freshness?.confirmedAppMetaSyncedAt, cached);
      }
    })();
  }, [
    applySnapshot,
    commandsEnabled,
    data,
    deviceId,
    installedVersion,
    isError,
    isFetching,
    isSuccess,
    markUnavailable,
    pollCommand,
    refetch,
    retryNonce,
    sendDeviceCommand,
  ]);

  useEffect(() => {
    const snap = confirmedPlayerSettingsFromDetail(data?.detail?.player);
    const syncedAt = data?.freshness?.confirmedAppMetaSyncedAt ?? null;
    if (
      !shouldApplyLiveGet({
        hydrated: hydratedRef.current,
        fieldIsTouched,
        isMutating: isMutatingUpdate,
        snapshotComplete: isCompleteConfirmedSettings(snap),
        incomingSyncedAt: syncedAt,
        lastAppliedSyncedAt: lastAppliedSyncedAtRef.current,
        applyGeneration: generationRef.current,
        currentGeneration: generationRef.current,
      })
    ) {
      return;
    }
    if (!snap) return;
    lastAppliedSyncedAtRef.current = syncedAt;
    applySnapshot(snap);
  }, [applySnapshot, data, fieldIsTouched, isMutatingUpdate]);

  const handleRetry = () => {
    generationRef.current += 1;
    reportStartedRef.current = false;
    hydratedRef.current = false;
    lastAppliedSyncedAtRef.current = null;
    confirmedAtConfirmRef.current = null;
    setSnapshotComplete(false);
    setLastConfirmedCopy(null);
    setOperatorState(null);
    setPendingRequest(null);
    setConfirmationUnavailable(false);
    setRetryNonce((n) => n + 1);
  };

  /**
   * After Confirm failure/timeout/exception: reconcile to Fleet confirmed.
   * Never silently retries update_kk_app_meta.
   */
  const reconcileAfterFailedConfirm = useCallback(async () => {
    setPendingRequest(null);
    try {
      const again = await refetch();
      const next = confirmedPlayerSettingsFromDetail(again.data?.detail?.player);
      const decision = decidePostConfirmReconcile({
        operatorState: "unsuccessful",
        fleetReachable: true,
        confirmedComplete: isCompleteConfirmedSettings(next),
      });
      if (decision.type === "apply-confirmed" && next) {
        applySnapshot(next);
        hydratedRef.current = true;
        lastAppliedSyncedAtRef.current =
          again.data?.freshness?.confirmedAppMetaSyncedAt ?? null;
        setConfirmationUnavailable(false);
        setOperatorState("unsuccessful");
        return;
      }
      setFieldIsTouched(false);
      setConfirmationUnavailable(true);
      setSnapshotComplete(false);
      setLastConfirmedCopy(REMOTE_SETTINGS_CONFIRMATION_UNAVAILABLE);
      setOperatorState("unavailable");
    } catch {
      setFieldIsTouched(false);
      setConfirmationUnavailable(true);
      setSnapshotComplete(false);
      setLastConfirmedCopy(REMOTE_SETTINGS_CONFIRMATION_UNAVAILABLE);
      setOperatorState("unavailable");
    }
  }, [applySnapshot, refetch]);

  const onValuesChange = (changed: Partial<InitialFormValues>) => {
    if (Object.prototype.hasOwnProperty.call(changed, "appLock")) {
      const next = localPinLifecycleOnAppLockChange(changed.appLock === true);
      if (next.appLockPin === null) {
        form.setFieldsValue({ appLockPin: null });
      }
      if (next.clearErrors) {
        form.setFields([{ name: "appLockPin", errors: [] }]);
      }
      if (next.runValidation) {
        void form.validateFields(["appLockPin"]).catch(() => undefined);
      }
    }
    setFieldIsTouched(form.isFieldsTouched());
  };

  const handleOnSubmit = async (values: InitialFormValues) => {
    if (!commandsEnabled || !deviceId) return;
    if (!snapshotComplete) return;
    if (values.volume == null || values.master == null) return;
    // Disable repeated Confirm while in flight (also via canConfirm / isMutatingUpdate).
    if (isMutatingUpdate) return;
    const pin =
      typeof values.appLockPin === "string" && values.appLockPin.trim()
        ? values.appLockPin.trim()
        : undefined;
    const startTime = values.startDate ? values.startDate.format("HH:mm") : "00:00";
    const endTime = values.endDate ? values.endDate.format("HH:mm") : "00:00";
    const requested = requestedFromConfirmValues({
      volume: values.volume,
      master: values.master,
      appLock: values.appLock === true,
      wakeSleep: values.wakeSleep === true,
      startTime,
      endTime,
      sleepSaturday: values.sleepSaturday === true,
      sleepSunday: values.sleepSunday === true,
    });
    confirmedAtConfirmRef.current = confirmedPlayerSettingsFromDetail(
      data?.detail?.player
    );
    setPendingRequest(requested);
    setConfirmationUnavailable(false);
    setOperatorState("sending");
    try {
      const sent = await sendDeviceCommand({
        deviceId,
        command: "update_kk_app_meta",
        settings: {
          volume: values.volume,
          master: values.master,
          appLock: values.appLock === true,
          appLockPin: pin,
          wakeSleep: values.wakeSleep === true,
          startTime,
          endTime,
          // T24 — only send weekend flags once device-confirmed (never invent false).
          ...(confirmedWeekend.sat !== null
            ? { sleepSaturday: values.sleepSaturday === true }
            : {}),
          ...(confirmedWeekend.sun !== null
            ? { sleepSunday: values.sleepSunday === true }
            : {}),
        },
      }).unwrap();
      const state = await pollCommand("update_kk_app_meta", sent.commandId);
      if (state === "successful") {
        setPendingRequest(null);
        hydratedRef.current = false;
        const again = await refetch();
        const next = confirmedPlayerSettingsFromDetail(again.data?.detail?.player);
        if (isCompleteConfirmedSettings(next)) {
          applySnapshot(next);
          hydratedRef.current = true;
          lastAppliedSyncedAtRef.current =
            again.data?.freshness?.confirmedAppMetaSyncedAt ?? null;
          setConfirmationUnavailable(false);
        } else {
          // ACK succeeded but Fleet GET incomplete — do not leave draft as applied.
          setFieldIsTouched(false);
          setConfirmationUnavailable(true);
          setSnapshotComplete(false);
          setLastConfirmedCopy(REMOTE_SETTINGS_CONFIRMATION_UNAVAILABLE);
          setOperatorState("unavailable");
        }
      } else {
        // Timeout / Unsuccessful — reconcile to Fleet confirmed; never resend.
        await reconcileAfterFailedConfirm();
      }
    } catch {
      // Transport / exception — reconcile or Confirmation unavailable; never resend.
      await reconcileAfterFailedConfirm();
    }
  };

  const pendingCompareCopy =
    operatorState === "sending"
      ? pendingRequestedVsConfirmedCopy({
          requested: pendingRequest,
          confirmed: confirmedAtConfirmRef.current,
        })
      : null;
  const statusText = confirmationUnavailable
    ? REMOTE_SETTINGS_CONFIRMATION_UNAVAILABLE
    : operatorLabel(operatorState, installedVersion, lastConfirmedCopy);
  const canConfirm = canConfirmRemoteSettings({
    commandsEnabled,
    snapshotComplete,
    fieldIsTouched,
    isMutatingUpdate,
    disabled,
  });

  return (
    <>
    <Form
      className={classNames({
        [styles.formDisabled]: formFullyLocked,
      })}
      requiredMark={false}
      layout="vertical"
      onValuesChange={onValuesChange}
      onFinish={handleOnSubmit}
      form={form}
    >
      <div className={styles.settingsHeadingContainer}>
        <div className="d-flex flex-column">
          <Text type="title" size="md">
            {title}
          </Text>
          <Text size="sm">
            These settings can only be applied to one Zone at a time.
          </Text>
          {statusText ? <Text size="sm">{statusText}</Text> : null}
          {pendingCompareCopy ? (
            <Text size="sm" className={styles.pendingCompare}>
              {pendingCompareCopy}
            </Text>
          ) : null}
        </div>
        <div className="d-flex align-items-center">
          {isMutatingUpdate && (
            <SyncOutlined className={styles.syncLoader} spin />
          )}

          {operatorState === "unavailable" && (
            <ButtonSmall
              type="button"
              className={styles.confirmButton}
              onClick={handleRetry}
            >
              {REMOTE_SETTINGS_RETRY_LABEL}
            </ButtonSmall>
          )}

          <ButtonSmall
            disabled={!canConfirm}
            type="submit"
            variant="disabled"
            className={styles.confirmButton}
          >
            Confirm
          </ButtonSmall>
        </div>
      </div>
      <ControlPanelActionRow
        className={classNames({ [styles.commandLocked]: commandSurfacesLocked })}
        leftComponent={<Text size="md">Volume Control</Text>}
        rightComponent={
          <Form.Item className={styles.formItem} name="volume">
            <Slider
              disabled={commandSurfacesLocked}
              tooltip={{
                placement: "bottom",
                formatter: (value) => `${value}%`,
              }}
            />
          </Form.Item>
        }
      />
      <ControlPanelActionRow
        className={classNames({ [styles.commandLocked]: commandSurfacesLocked })}
        leftComponent={<Text size="md">Master Level Control</Text>}
        rightComponent={
          <Form.Item className={styles.formItem} name="master">
            <Slider
              disabled={commandSurfacesLocked}
              tooltip={{
                placement: "bottom",
                formatter: (value) => `${value}%`,
              }}
            />
          </Form.Item>
        }
      />

      <ControlPanelActionRow
        borderBottom={false}
        leftComponent={<Text size="md">App Lock</Text>}
        rightComponent={
          <div
            className={classNames({
              [styles.localPinEnabled]: allowLocalPin,
            })}
          >
            <Form.Item
              className={`d-flex justify-content-end ${styles.formItem}`}
              name="appLock"
              valuePropName="checked"
            >
              <BrandSwitch
                toggleText={{
                  on: "On",
                  off: "Off",
                }}
              />
            </Form.Item>
          </div>
        }
      />

      <ControlPanelActionRow
        leftComponent={<div />}
        rightComponent={
          <div
            className={classNames({
              [styles.localPinEnabled]: allowLocalPin,
            })}
          >
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => (
                <Form.Item
                  label={<Text size="md">App Lock PIN</Text>}
                  className={styles.formItem}
                  layout="horizontal"
                  name="appLockPin"
                  validateTrigger={["onChange", "onBlur"]}
                  labelCol={{
                    style: {
                      display: "flex",
                      justifyContent: "space-between",
                      flex: 1,
                    },
                  }}
                  rules={[
                    {
                      required: isPinRequired(
                        getFieldValue("appLock") === true,
                        confirmedPinSet
                      ),
                      message: PIN_REQUIRED_MESSAGE,
                    },
                    {
                      pattern: PIN_PATTERN,
                      message: PIN_PATTERN_MESSAGE,
                    },
                  ]}
                >
                  <Input
                    size="small"
                    placeholder="Enter 4-digit PIN"
                    className="settings-pin-input"
                    maxLength={4}
                    disabled={
                      !isPinInputEnabled(
                        getFieldValue("appLock") === true,
                        allowLocalPin
                      )
                    }
                    width={"100%"}
                  />
                </Form.Item>
              )}
            </Form.Item>
          </div>
        }
      />

      <ControlPanelActionRow
        borderBottom={false}
        className={classNames({ [styles.commandLocked]: commandSurfacesLocked })}
        leftComponent={<Text size="md">Wake/Sleep</Text>}
        rightComponent={
          <Form.Item
            layout="horizontal"
            className={`d-flex justify-content-end ${styles.formItem}`}
            name="wakeSleep"
            valuePropName="checked"
          >
            <BrandSwitch
              toggleText={{
                on: "On",
                off: "Off",
              }}
            />
          </Form.Item>
        }
      />

      <ControlPanelActionRow
        borderBottom={false}
        className={classNames({ [styles.commandLocked]: commandSurfacesLocked })}
        leftComponent={<div />}
        rightComponent={
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => (
              <Form.Item
                label={<Text size="md">{WAKE_TIME_LABEL}</Text>}
                className={styles.formItem}
                layout="horizontal"
                name="startDate"
                rules={[
                  {
                    required: getFieldValue("wakeSleep"),
                    message: "Select a wake time",
                  },
                ]}
                labelCol={{
                  style: {
                    display: "flex",
                    justifyContent: "space-between",
                    flex: 1,
                  },
                }}
              >
                <TimePicker
                  className="settings-time-picker"
                  format="hh:mm A"
                  minuteStep={5}
                  style={{ width: "100%" }}
                  use12Hours
                  disabled={!getFieldValue("wakeSleep") || commandSurfacesLocked}
                  placeholder="Select Time"
                />
              </Form.Item>
            )}
          </Form.Item>
        }
      />
      <ControlPanelActionRow
        borderBottom={false}
        className={classNames({ [styles.commandLocked]: commandSurfacesLocked })}
        leftComponent={<div />}
        rightComponent={
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => (
              <Form.Item
                label={<Text size="md">{SLEEP_TIME_LABEL}</Text>}
                className={styles.formItem}
                layout="horizontal"
                name="endDate"
                rules={[
                  {
                    required: getFieldValue("wakeSleep"),
                    message: "Select a sleep time",
                  },
                ]}
                labelCol={{
                  style: {
                    display: "flex",
                    justifyContent: "space-between",
                    flex: 1,
                  },
                }}
              >
                <TimePicker
                  className="settings-time-picker"
                  format="hh:mm A"
                  minuteStep={5}
                  style={{ width: "100%" }}
                  use12Hours
                  disabled={!getFieldValue("wakeSleep") || commandSurfacesLocked}
                  placeholder="Select Time"
                />
              </Form.Item>
            )}
          </Form.Item>
        }
      />

      {/* T24 — Weekend Sleep (after Sleep Time). Enabled only when Wake/Sleep is on. */}
      {(
        [
          { name: "sleepSaturday", label: "Sleep on Saturday", confirmed: confirmedWeekend.sat },
          { name: "sleepSunday", label: "Sleep on Sunday", confirmed: confirmedWeekend.sun },
        ] as const
      ).map(({ name, label, confirmed }) => (
        <ControlPanelActionRow
          key={name}
          borderBottom={false}
          className={classNames({ [styles.commandLocked]: commandSurfacesLocked })}
          leftComponent={
            <div className="d-flex flex-column">
              <Text size="md">{label}</Text>
              {snapshotComplete && confirmed === null ? (
                <Text size="sm" className={styles.pendingCompare}>
                  {WEEKEND_NOT_REPORTED} by device
                </Text>
              ) : null}
            </div>
          }
          rightComponent={
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => (
                <Form.Item
                  layout="horizontal"
                  className={`d-flex justify-content-end ${styles.formItem}`}
                  name={name}
                  valuePropName="checked"
                >
                  <BrandSwitch
                    toggleText={{
                      on: "On",
                      off: "Off",
                    }}
                    disabled={!getFieldValue("wakeSleep") || commandSurfacesLocked}
                  />
                </Form.Item>
              )}
            </Form.Item>
          }
        />
      ))}
    </Form>
    {/* T24 — Music Scheduler subsection (own Confirm → update_kk_music_schedule). */}
    {deviceId ? (
      <MusicSchedulerSection
        deviceId={deviceId}
        commandsEnabled={commandsEnabled}
        disabled={disabled}
        externalBusy={appMetaBusy}
        data={data}
        isFetching={isFetching}
        isError={isError}
        isSuccess={isSuccess}
        refetch={refetch}
        onBusyChange={setMusicBusy}
      />
    ) : null}
    </>
  );
};
